import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../../components/ui/accordion';
import { 
  MapPin, Building2, Package, RefreshCw, Download,
  ChevronRight, Users, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const PRIORIDADE_COLORS = {
  'Alta': 'bg-red-100 text-red-700 border-red-300',
  'Média': 'bg-amber-100 text-amber-700 border-amber-300',
  'Baixa': 'bg-green-100 text-green-700 border-green-300'
};

export default function RelatorioNecessidadesLocalizacaoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroDistrito, setFiltroDistrito] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('Aprovada');
  const [distritos, setDistritos] = useState([]);

  useEffect(() => {
    loadDistritos();
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [filtroDistrito, filtroStatus]);

  const loadDistritos = async () => {
    try {
      const res = await apiClient.get('/distritos');
      setDistritos(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar distritos:', e);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroDistrito) params.append('distrito_id', filtroDistrito);
      if (filtroStatus) params.append('status', filtroStatus);
      
      const response = await apiClient.get(`/relatorios/necessidades/por-localizacao?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart data by district
  const chartData = data?.necessidades_por_localizacao?.map(d => ({
    name: d.distrito_nome?.substring(0, 12) || 'N/A',
    quantidade: d.total_quantidade,
    pedidos: d.total_necessidades
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relatorio-necessidades-localizacao">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Necessidades por Localização</h1>
          <p className="text-slate-500">
            Necessidades discriminadas por distrito, concelho e instituição
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Distritos</p>
                <p className="text-2xl font-bold text-blue-600">
                  {data?.resumo?.total_distritos || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Necessidades</p>
                <p className="text-2xl font-bold text-purple-600">
                  {data?.resumo?.total_necessidades || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Itens Necessários</p>
                <p className="text-2xl font-bold text-amber-600">
                  {data?.resumo?.total_quantidade?.toLocaleString('pt-PT') || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Data Relatório</p>
                <p className="text-sm font-medium text-slate-700">
                  {data?.data_geracao ? new Date(data.data_geracao).toLocaleString('pt-PT') : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={filtroDistrito || "__all__"} onValueChange={(v) => setFiltroDistrito(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Todos os Distritos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os Distritos</SelectItem>
                {distritos.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus || "__all__"} onValueChange={(v) => setFiltroStatus(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="Pendente">Pendente</SelectItem>
                <SelectItem value="Aprovada">Aprovada</SelectItem>
                <SelectItem value="Satisfeita">Satisfeita</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quantidade por Distrito</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    value.toLocaleString('pt-PT'), 
                    name === 'quantidade' ? 'Itens' : 'Pedidos'
                  ]} 
                />
                <Bar dataKey="quantidade" fill="#0066CC" name="Itens" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Hierarchical Data */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhe por Localização</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.necessidades_por_localizacao?.length > 0 ? (
            <Accordion type="multiple" className="w-full">
              {data.necessidades_por_localizacao.map((distrito, dIdx) => (
                <AccordionItem key={dIdx} value={`distrito-${dIdx}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-4 w-full">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold">{distrito.distrito_nome}</span>
                      <Badge variant="outline" className="ml-auto mr-4">
                        {distrito.total_necessidades} pedidos | {distrito.total_quantidade?.toLocaleString('pt-PT')} itens
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-9 space-y-4">
                      {distrito.por_concelho?.map((concelho, cIdx) => (
                        <Accordion key={cIdx} type="multiple" className="border rounded-lg">
                          <AccordionItem value={`concelho-${dIdx}-${cIdx}`} className="border-0">
                            <AccordionTrigger className="px-4 hover:no-underline">
                              <div className="flex items-center gap-3">
                                <Building2 className="w-4 h-4 text-green-600" />
                                <span>{concelho.concelho_nome || 'Concelho não especificado'}</span>
                                <Badge variant="secondary" className="ml-2">
                                  {concelho.total_quantidade?.toLocaleString('pt-PT')} itens
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-4">
                                {concelho.instituicoes?.map((inst, iIdx) => (
                                  <div key={iIdx} className="border rounded-lg p-4 bg-slate-50">
                                    <div className="flex items-center gap-2 mb-3">
                                      <Building2 className="w-4 h-4 text-slate-500" />
                                      <span className="font-medium">{inst.instituicao_nome}</span>
                                      {inst.instituicao_codigo && (
                                        <Badge variant="outline" className="text-xs">
                                          {inst.instituicao_codigo}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="space-y-2">
                                      {inst.necessidades?.map((nec, nIdx) => (
                                        <div 
                                          key={nIdx} 
                                          className="flex items-center justify-between p-3 bg-white rounded border"
                                        >
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium">{nec.tipo_recurso}</span>
                                              {nec.categoria && (
                                                <Badge variant="outline" className="text-xs">
                                                  {nec.categoria}
                                                </Badge>
                                              )}
                                              {nec.prioridade && (
                                                <Badge className={PRIORIDADE_COLORS[nec.prioridade] || ''}>
                                                  {nec.prioridade}
                                                </Badge>
                                              )}
                                            </div>
                                            {/* Demographics */}
                                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                              {nec.demografia?.sexo_label && nec.demografia.sexo_label !== 'Não especificado' && (
                                                <span className="flex items-center gap-1">
                                                  <Users className="w-3 h-3" />
                                                  {nec.demografia.sexo_label}
                                                </span>
                                              )}
                                              {nec.demografia?.faixa_etaria_label && nec.demografia.faixa_etaria_label !== 'Não especificado' && (
                                                <span>{nec.demografia.faixa_etaria_label}</span>
                                              )}
                                              {nec.demografia?.tamanho && (
                                                <span>Tam: {nec.demografia.tamanho}</span>
                                              )}
                                            </div>
                                            {nec.justificacao && (
                                              <p className="text-xs text-slate-400 mt-1 italic">
                                                {nec.justificacao}
                                              </p>
                                            )}
                                          </div>
                                          <div className="text-right ml-4">
                                            <div className="text-lg font-bold text-blue-600">
                                              {nec.quantidade_necessaria?.toLocaleString('pt-PT')}
                                            </div>
                                            {nec.quantidade_alocada > 0 && (
                                              <div className="text-xs text-green-600">
                                                {nec.quantidade_alocada} alocados
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhuma necessidade encontrada com os filtros aplicados</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
